import React, { Component } from 'react'
import { Modal, Form, Checkbox, Button, Icon, notification, Spin } from 'antd'
import { firebase, db } from '../../firebase/Firebase'
import ColType from '../../Types'
import PersonAvatar from '../../customcomponents/PersonAvatar'

// One read aloud setting per subject. Add new subjects here and they
// will show up in the modal and be saved automatically.
const READ_ALOUD_SUBJECTS = [
    { key: 'enableReadAloudELA', label: 'ELA' },
    { key: 'enableReadAloudMath', label: 'Math' },
    { key: 'enableReadAloudScience', label: 'Science' },
    { key: 'enableReadAloudSocialStudies', label: 'Social Studies' },
]

// Default values so older docs without a key still get a value.
const DEFAULT_SETTINGS = READ_ALOUD_SUBJECTS.reduce((acc, subject) => {
    acc[subject.key] = false
    return acc
}, {})

class StudentSettingsModal extends Component {
    _unmounted = false

    state = {
        visible: false,
        loading: false,
        saving: false,
        settings: { ...DEFAULT_SETTINGS },
    }

    componentWillUnmount() {
        this._unmounted = true
    }

    getStudentId = () => {
        const { student } = this.props
        return student && student.id ? student.id : null
    }

    isReadOnly = () => {
        return this.props.readOnly
    }

    showModal = () => {
        this.setState({ visible: true }, () => {
            this.loadSettings()
        })
    }

    hideModal = () => {
        if (this.state.saving) return
        this.setState({ visible: false })
    }

    loadSettings = () => {
        const studentId = this.getStudentId()
        if (!studentId) return

        this.setState({ loading: true })

        db.collection(ColType.studentSettings)
            .doc(studentId)
            .get()
            .then((doc) => {
                if (this._unmounted) return
                const data = doc.exists ? doc.data() : {}
                this.setState({
                    settings: { ...DEFAULT_SETTINGS, ...data },
                    loading: false,
                })
            })
            .catch((error) => {
                console.error('Error loading student settings', error)
                if (this._unmounted) return
                this.setState({ loading: false })
            })
    }

    onSettingChange = (key, checked) => {
        const settings = { ...this.state.settings }
        settings[key] = checked
        this.setState({ settings: settings })
    }

    saveSettings = () => {
        const studentId = this.getStudentId()
        const { student, districtId } = this.props
        if (!studentId || !districtId || this.isReadOnly()) return

        this.setState({ saving: true })

        const settingsToSave = {
            studentId: studentId,
            districtId: districtId,
            updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
        }
        READ_ALOUD_SUBJECTS.forEach((subject) => {
            settingsToSave[subject.key] = !!this.state.settings[subject.key]
        })

        db.collection(ColType.studentSettings)
            .doc(studentId)
            .set(settingsToSave, { merge: true })
            .then(() => {
                if (this._unmounted) return
                this.setState({ saving: false, visible: false })
                notification.success({
                    message: 'Settings saved',
                    description: student.firstName ?
                        'Accessibility settings for ' + student.firstName + ' have been saved.' :
                        'Accessibility settings have been saved.',
                    placement: 'bottomRight',
                })
                if (this.props.onSaved) {
                    this.props.onSaved(settingsToSave)
                }
            })
            .catch((error) => {
                console.error('Error saving student settings', error)
                if (this._unmounted) return
                this.setState({ saving: false })
                notification.error({
                    message: 'Settings were not saved',
                    description: 'Check your connection and select Save settings again.',
                    placement: 'bottomRight',
                })
            })
    }

    render() {
        const { student } = this.props
        const { visible, loading, saving, settings } = this.state
        const readOnly = this.isReadOnly()

        if (!student) return null

        return (
            <div className="inline-block">
                <Button size="large" onClick={this.showModal}>
                    <Icon type="setting" />
                    <span>Accessibility settings</span>
                </Button>

                <Modal
                    title="Student Accessibility Settings"
                    visible={visible}
                    onCancel={this.hideModal}
                    destroyOnClose={true}
                    footer={[
                        <Button key="cancel" size="large" onClick={this.hideModal} disabled={saving}>
                            Cancel
                        </Button>,
                        <Button
                            key="save"
                            type="primary"
                            size="large"
                            onClick={this.saveSettings}
                            loading={saving}
                            disabled={loading || readOnly}
                        >
                            Save settings
                        </Button>,
                    ]}
                >
                    <div className="mb-3">
                        <PersonAvatar
                            person={student}
                            size={'large'}
                            containerClassName="font-25"
                            avatarClassName="font-25 font-bold"
                            personClassName="font-bold"
                        />
                    </div>

                    <Spin spinning={loading}>
                        <Form layout="vertical" className='font-black'>
                            <div className="font-20 font-bold mb-1">Read aloud</div>
                            <div className="font-16 mb-2">
                                Choose the subjects where this student gets read aloud support.
                            </div>

                            {READ_ALOUD_SUBJECTS.map((subject) => {
                                return (
                                    <Form.Item key={subject.key} className="mb-1">
                                        <Checkbox
                                            checked={!!settings[subject.key]}
                                            onChange={(e) => this.onSettingChange(subject.key, e.target.checked)}
                                            disabled={loading || saving || readOnly}
                                        >
                                            <span className="font-16 ml-1 font-black font-normal">
                                                {'Enable read aloud for ' + subject.label}
                                            </span>
                                        </Checkbox>
                                    </Form.Item>
                                )
                            })}

                            <div
                                className="p-2 br-4 mt-2"
                                style={{ background: '#f5f7fa', border: '1px solid #e6e9ef' }}
                            >
                                <div className="flex flex-v-center mb-1">
                                    <Icon type="sound" className="mr-1" />
                                    <span className="font-16 font-bold">What this does</span>
                                </div>
                                <div className="font-16">
                                    When read aloud is on for a subject, quizzes in that subject will show
                                    a text to speech button next to each question and answer choice. The
                                    student can select the button to hear the text read aloud as many times
                                    as they need.
                                </div>
                                <div className="font-16 mt-1">
                                    Turn this on for subjects where the student has a read aloud
                                    accommodation, or where hearing the text helps them show what they know.
                                </div>
                            </div>

                            {readOnly &&
                                <div className="font-16 mt-2" style={{ color: '#8c8c8c' }}>
                                    You can view these settings, but only the student's teacher can change them.
                                </div>
                            }
                        </Form>
                    </Spin>
                </Modal>
            </div>
        )
    }
}

export default StudentSettingsModal